import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const HybridLedger = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [status, setStatus] = useState(null);
  const [loans, setLoans] = useState([]);
  const [repos, setRepos] = useState([]);
  const [events, setEvents] = useState([]);
  const [filings, setFilings] = useState([]);
  const [selectedFiling, setSelectedFiling] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.is_admin;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch status
      const statusRes = await fetch(`${API}/api/hybrid-ledger/status`);
      const statusData = await statusRes.json();
      setStatus(statusData);
      
      // Fetch loans
      const loansRes = await fetch(`${API}/api/hybrid-ledger/loans`);
      const loansData = await loansRes.json();
      setLoans(loansData.loans || []);
      
      // Fetch repos
      const reposRes = await fetch(`${API}/api/hybrid-ledger/repos`);
      const reposData = await reposRes.json();
      setRepos(reposData.repos || []);
      
      // Fetch events
      const eventsRes = await fetch(`${API}/api/hybrid-ledger/events?limit=50`);
      const eventsData = await eventsRes.json();
      setEvents(eventsData.events || []);
      
      // Fetch Form D filings
      const filingsRes = await fetch(`${API}/api/hybrid-ledger/form-d/filings`);
      const filingsData = await filingsRes.json();
      setFilings(filingsData.filings || []);
      
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const startSync = async () => {
    try {
      const res = await fetch(`${API}/api/hybrid-ledger/sync/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      alert(data.message);
      fetchData();
    } catch (err) {
      console.error('Error starting sync:', err);
    }
  };

  const stopSync = async () => {
    try {
      const res = await fetch(`${API}/api/hybrid-ledger/sync/stop`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      alert(data.message);
      fetchData();
    } catch (err) {
      console.error('Error stopping sync:', err);
    }
  };

  const manualSync = async () => {
    try {
      const res = await fetch(`${API}/api/hybrid-ledger/sync/manual`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      alert(data.message);
      fetchData();
    } catch (err) {
      console.error('Error manual sync:', err);
    }
  };

  const viewFormD = async (filing) => {
    try {
      const res = await fetch(`${API}/api/hybrid-ledger/form-d/${filing.filing_type}/${filing.reference_id}`);
      const data = await res.json();
      setSelectedFiling(data);
    } catch (err) {
      console.error('Error fetching Form D:', err);
    }
  };

  const downloadFormD = () => {
    if (!selectedFiling) return;
    
    const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${selectedFiling.xml_content}`], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `FormD_${selectedFiling.filing_type}_${selectedFiling.reference_id}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(num || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-white flex items-center justify-center">
        <div className="text-xl">Loading Hybrid Ledger...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white" data-testid="hybrid-ledger-page">
      {/* Header */}
      <header className="bg-[#0d1421] border-b border-gray-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white">
              ← Back
            </button>
            <h1 className="text-xl font-semibold">Hybrid Ledger System</h1>
            <span className={`px-2 py-1 rounded text-xs ${status?.contract_available ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
              {status?.contract_available ? 'CONTRACT ACTIVE' : 'DATABASE MODE'}
            </span>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              {status?.stats?.is_polling ? (
                <button onClick={stopSync} className="px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded text-sm">
                  Stop Sync
                </button>
              ) : (
                <button onClick={startSync} className="px-3 py-1.5 bg-green-600 hover:bg-green-500 rounded text-sm" disabled={!status?.contract_available}>
                  Start Sync
                </button>
              )}
              <button onClick={manualSync} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded text-sm">
                Manual Sync
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-5 gap-4 mb-8">
          <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-4">
            <div className="text-3xl font-bold text-amber-400">{status?.stats?.loans?.total || 0}</div>
            <div className="text-gray-400 text-sm">On-Chain Loans</div>
          </div>
          <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-4">
            <div className="text-3xl font-bold text-blue-400">{status?.stats?.repos?.total || 0}</div>
            <div className="text-gray-400 text-sm">On-Chain Repos</div>
          </div>
          <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-4">
            <div className="text-3xl font-bold text-green-400">{status?.stats?.form_d_filings || 0}</div>
            <div className="text-gray-400 text-sm">Form D Filings</div>
          </div>
          <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-4">
            <div className="text-3xl font-bold text-purple-400">{status?.stats?.events_processed || 0}</div>
            <div className="text-gray-400 text-sm">Events Processed</div>
          </div>
          <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-4">
            <div className="text-xl font-bold text-cyan-400">{status?.stats?.last_synced_block || 'N/A'}</div>
            <div className="text-gray-400 text-sm">Last Block</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-800 pb-4">
          {['overview', 'loans', 'repos', 'events', 'form-d'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium capitalize ${
                activeTab === tab 
                  ? 'bg-amber-600 text-white' 
                  : 'bg-[#151d2e] text-gray-400 hover:text-white'
              }`}
            >
              {tab === 'form-d' ? 'SEC Form D' : tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold mb-4">System Architecture</h3>
              <div className="grid grid-cols-3 gap-6">
                <div className="bg-[#151d2e] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${status?.blockchain_connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="font-medium">Ethereum Mainnet</span>
                  </div>
                  <p className="text-sm text-gray-400">Blockchain layer for immutable records</p>
                </div>
                <div className="bg-[#151d2e] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${status?.contract_available ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                    <span className="font-medium">Smart Contract</span>
                  </div>
                  <p className="text-sm text-gray-400">{status?.contract_address || 'Awaiting deployment'}</p>
                </div>
                <div className="bg-[#151d2e] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="font-medium">Off-Chain Database</span>
                  </div>
                  <p className="text-sm text-gray-400">MongoDB for performance & queries</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold mb-4">Features</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${status?.features?.event_polling ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    ⚡
                  </div>
                  <div>
                    <div className="font-medium">Event Polling</div>
                    <div className="text-sm text-gray-400">Real-time blockchain sync</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${status?.features?.form_d_generation ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    📄
                  </div>
                  <div>
                    <div className="font-medium">Form D Generation</div>
                    <div className="text-sm text-gray-400">SEC-compliant XML filings</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${status?.features?.off_chain_sync ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    🔄
                  </div>
                  <div>
                    <div className="font-medium">Off-Chain Sync</div>
                    <div className="text-sm text-gray-400">Database mirroring</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-500/20 text-amber-400">
                    🔐
                  </div>
                  <div>
                    <div className="font-medium">Regulation D Compliance</div>
                    <div className="text-sm text-gray-400">Rule 506(b) exemption</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loans Tab */}
        {activeTab === 'loans' && (
          <div className="bg-[#0d1421] rounded-xl border border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#151d2e]">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Borrower</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Collateral</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Synced</th>
                </tr>
              </thead>
              <tbody>
                {loans.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No on-chain loans synced yet
                    </td>
                  </tr>
                ) : (
                  loans.map(loan => (
                    <tr key={loan.onchain_id} className="border-t border-gray-800 hover:bg-[#151d2e]">
                      <td className="px-4 py-3 text-sm font-mono">{loan.onchain_id}</td>
                      <td className="px-4 py-3 text-sm font-mono">{loan.borrower?.slice(0, 10)}...</td>
                      <td className="px-4 py-3 text-sm">{formatNumber(loan.amount_beth)} BETH</td>
                      <td className="px-4 py-3 text-sm">{formatNumber(loan.collateral_beth)} BETH</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          loan.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          loan.status === 'repaid' ? 'bg-gray-500/20 text-gray-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>{loan.status}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">{formatDate(loan.synced_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Repos Tab */}
        {activeTab === 'repos' && (
          <div className="bg-[#0d1421] rounded-xl border border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#151d2e]">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Seller</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Repurchase</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Rate</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {repos.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No on-chain repos synced yet
                    </td>
                  </tr>
                ) : (
                  repos.map(repo => (
                    <tr key={repo.onchain_id} className="border-t border-gray-800 hover:bg-[#151d2e]">
                      <td className="px-4 py-3 text-sm font-mono">{repo.onchain_id}</td>
                      <td className="px-4 py-3 text-sm font-mono">{repo.seller?.slice(0, 10)}...</td>
                      <td className="px-4 py-3 text-sm">{formatNumber(repo.amount_beth)} BETH</td>
                      <td className="px-4 py-3 text-sm">{formatNumber(repo.repurchase_price_beth)} BETH</td>
                      <td className="px-4 py-3 text-sm">{repo.implied_rate}%</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          repo.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          repo.status === 'executed' ? 'bg-gray-500/20 text-gray-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>{repo.status}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="bg-[#0d1421] rounded-xl border border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#151d2e]">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Event</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Data</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Block</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Time</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      No blockchain events processed yet
                    </td>
                  </tr>
                ) : (
                  events.map((event, idx) => (
                    <tr key={idx} className="border-t border-gray-800 hover:bg-[#151d2e]">
                      <td className="px-4 py-3 text-sm font-medium text-amber-400">{event.event_name}</td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-400">{JSON.stringify(event.data).slice(0, 50)}...</td>
                      <td className="px-4 py-3 text-sm">{event.block_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-400">{formatDate(event.logged_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Form D Tab */}
        {activeTab === 'form-d' && (
          <div className="grid grid-cols-2 gap-6">
            {/* Filings List */}
            <div className="bg-[#0d1421] rounded-xl border border-gray-800 overflow-hidden">
              <div className="bg-[#151d2e] px-4 py-3">
                <h3 className="font-semibold">SEC Form D Filings</h3>
              </div>
              <div className="p-4 space-y-3">
                {filings.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No Form D filings yet</p>
                ) : (
                  filings.map((filing, idx) => (
                    <div
                      key={idx}
                      onClick={() => viewFormD(filing)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedFiling?.reference_id === filing.reference_id
                          ? 'bg-amber-600/20 border border-amber-600'
                          : 'bg-[#151d2e] hover:bg-[#1a2538]'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium capitalize">{filing.filing_type} Filing</div>
                          <div className="text-sm text-gray-400 font-mono">{filing.reference_id}</div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          filing.status === 'generated' ? 'bg-green-500/20 text-green-400' :
                          filing.status === 'submitted' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>{filing.status}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{formatDate(filing.created_at)}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Filing Details */}
            <div className="bg-[#0d1421] rounded-xl border border-gray-800 overflow-hidden">
              <div className="bg-[#151d2e] px-4 py-3 flex justify-between items-center">
                <h3 className="font-semibold">Filing Details</h3>
                {selectedFiling && (
                  <button
                    onClick={downloadFormD}
                    className="px-3 py-1 bg-amber-600 hover:bg-amber-500 rounded text-sm"
                  >
                    Download XML
                  </button>
                )}
              </div>
              <div className="p-4">
                {selectedFiling ? (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-400">Filing Type</label>
                      <div className="font-medium capitalize">{selectedFiling.filing_type}</div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Reference ID</label>
                      <div className="font-mono">{selectedFiling.reference_id}</div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Status</label>
                      <div className="font-medium">{selectedFiling.status}</div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">XML Preview</label>
                      <pre className="bg-[#151d2e] p-3 rounded-lg text-xs overflow-auto max-h-64 mt-1">
                        {selectedFiling.xml_content?.slice(0, 1500)}...
                      </pre>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Select a filing to view details</p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default HybridLedger;
